import React, { memo, useRef, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { Col, Form, Row } from 'react-bootstrap';
import NoteDropdown from './noteDropdown';
import { useClickAway } from 'react-use';
import {
  getSides,
  checkDoctorDept,
  getDragDropStyle,
  isHiddenInPrint,
  itemBottomGapStyle,
  itemTitleStyle,
  itemSettingStyle,
  lineHeightStyle,
  noteInline,
  isNoteUnderLine,
} from '../../../utils/helpers';
import CommonDeleteBtn from './commonDeleteBtn';
import SelectedItemSearch from './views/selectedItemSearch';
import { useSelector } from 'react-redux';
import DiagnosisDeleteBtn from './diagnosisDeleteBtn';

const DiagnosisView = ({
  selectedDiagnosis,
  setSelectedDiagnosis,
  setShowDiagnosis,
  prescriptionItems,
  deletePrescriptionItem,
  selectedStage,
  element,
  isHistoryPage = false,
}) => {
  const userInfo = useSelector((state) => state.authReducer.data);
  const specialties = useSelector((state) => state.specialtyReducer.specialties);
  const allNotes = useSelector((state) => state?.prescriptionReducer?.prescriptionInfo?.itemElementNotes || []);
  const { stage, isShow } = selectedStage || {};
  const [isEdit, setIsEdit] = useState('');

  const onDragEnd = (result) => {
    if (!result.destination) {
      return;
    }
    const reorderedItems = reorder(
      selectedDiagnosis,
      result.source.index,
      result.destination.index,
    );
    setSelectedDiagnosis(reorderedItems);
  };

  // this function to help us with reordering the result
  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  const handleExtras = (item, fieldName, value) => {
    const updatedDiagnosis = selectedDiagnosis.map((diagnosis) =>
      diagnosis.name === item.name
        ? { ...diagnosis, [fieldName]: value }
        : diagnosis,
    );
    setSelectedDiagnosis(updatedDiagnosis);
  };

  const ref = useRef(null);
  useClickAway(ref, (e) => {
    setIsEdit('');
  });

  const editMode = (data) => {
    return (
      <Row ref={ref} className="selected-row-edit" key={data?.name}>
        <Col md={4} className="pl-0">
          <SelectedItemSearch
            apiUrl="diagnoses"
            selectedItemName={data.name}
            selectedItems={selectedDiagnosis}
            selectedItemsAction={setSelectedDiagnosis}
          />
        </Col>
        <Col md={2} className="pl-0">
          <Form.Check
            type="checkbox"
            id="query"
            label="Query"
            className="pt-1"
            checked={data?.query}
            onChange={(evt) => handleExtras(data, 'query', evt.target.checked)}
          />
        </Col>
        <Col md={2} className="pl-0">
          <Form.Select
            className="form-control form-control-sm"
            defaultValue={data?.side}
            onChange={(e) => handleExtras(data, 'side', e.target.value)}
          >
            <option value="">Side</option>
            {getSides(
              checkDoctorDept(
                'opthalmology',
                userInfo?.speciality[0],
                specialties,
              ),
            ).map((side) =>
              Object.entries(side).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              )),
            )}
          </Form.Select>
        </Col>
        <Col md={3} className="pl-0 pr-0">
          <NoteDropdown
            value={data?.note || ''}
            onChange={(val) => handleExtras(data, 'note', val)}
            placeholder="Add Note"
            suggestions={allNotes.filter(
              (n) => n.elementType === 'diagnosis' && n.itemElement?.toLowerCase() === data?.name?.toLowerCase(),
            )}
          />
        </Col>
        <Col md={1} className="text-right">
          <i
            className="far fa-times-circle"
            aria-hidden="true"
            onClick={() => setIsEdit('')}
          ></i>
        </Col>
      </Row>
    );
  };

  const prescriptionItemCSSObject = () => itemBottomGapStyle(element, selectedDiagnosis.length === 0);

  return (
    <div
      className={`row item-row ${selectedDiagnosis.length ? 'data-item' : ''}`}
      key={selectedDiagnosis.name}
      style={prescriptionItemCSSObject()}
    >
      <span
        className={`prescription-item ${
          isHiddenInPrint(prescriptionItems, 'diagnosis')
            ? 'item-title-hide'
            : ''
        }`}
        style={itemTitleStyle(
          prescriptionItems?.items?.['diagnosis']?.titleStyle || {},
        )}
        {...(!isHistoryPage
          ? {
              onClick: (e) => {
                e.stopPropagation();
                setShowDiagnosis(true);
              },
            }
          : {})}
      >
        {element?.alterName?.length ? (
          <span>{element?.alterName}</span>
        ) : (
          <span>Diagnosis</span>
        )}
      </span>
      {selectedDiagnosis?.length > 0 ? (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable">
            {(provided, snapshot) => (
              <ul
                className="sub-child-list drag-drop"
                {...provided.droppableProps}
                ref={provided.innerRef}
                style={lineHeightStyle('diagnosis', prescriptionItems)}
              >
                {selectedDiagnosis.length > 0 &&
                  selectedDiagnosis?.map((item, index) => (
                    <Draggable
                      key={item.name}
                      draggableId={item.name}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <li
                          key={item.name}
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          style={{
                            ...getDragDropStyle(
                              snapshot.isDragging,
                              provided.draggableProps.style,
                            ),
                            position: 'relative',
                          }}
                        >
                          <div onClick={() => setIsEdit(index)}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span
                                style={itemSettingStyle(
                                  prescriptionItems?.items?.['diagnosis']
                                    ?.itemStyle || {},
                                )}
                              >
                                {item.query ? '?' : ''} {item.query} {item.name}{' '}
                                {item?.side
                                  ? ` - ${item?.side?.toUpperCase()}`
                                  : ''}
                                {noteInline(item.note, 'diagnosis', prescriptionItems)}
                              </span>
                              {!isHistoryPage && (
                                <DiagnosisDeleteBtn
                                  action={deletePrescriptionItem}
                                  itemName={'diagnosis'}
                                  delId={item.name}
                                />
                              )}
                            </div>
                            {item.note && isNoteUnderLine('diagnosis', prescriptionItems) && (
                              <div
                                className="pl-2 preline"
                                style={itemSettingStyle(
                                  prescriptionItems?.items?.['diagnosis']
                                    ?.subItemStyle || {},
                                )}
                              >
                                {item?.note}
                              </div>
                            )}
                          </div>
                          {index === isEdit && editMode(item)}
                        </li>
                      )}
                    </Draggable>
                  ))}
                {provided.placeholder}

                {stage && isShow && (
                  <li key={stage}>
                    <div
                      style={itemSettingStyle(
                        prescriptionItems?.items?.['diagnosis']?.itemStyle ||
                          {},
                      )}
                    >
                      Stage: {stage}
                    </div>
                  </li>
                )}
              </ul>
            )}
          </Droppable>
        </DragDropContext>
      ) : (
        ''
      )}
    </div>
  );
};

export default memo(DiagnosisView);
